import { useConfiguration } from '../../../hooks/useConfiguration';
import useLanguage from '../../../hooks/useLanguage';
import {
    getFilteredAddons,
    getSubscrebedFreeAddons,
    getSubscrebedPaidAddons,
    isStaticItem,
    transformStaticCarouselItem,
    transformStaticItem,
} from '../utils/addonsUtils';
import { useAllSubscriptionTier } from './useAllSubscriptionTier';
import { useBundleAndSaveRules } from './useBundleAndSaveRules';
import user from '../../../helpers/user';
import { __ } from '../../../translations';
import { getDefaultServiceCollectionDetails, isUserInGracePeriod } from '@intigral/utils';
import { ADDON_STATUS } from '@intigral/constant';
import { BUNDLE_AND_SAVE, PICK_AND_CHOOSE } from '../constants';
import { IPlanConfigV2 } from '@intigral/domain/lib/entity/interface/iEntitlements';
import { isBeforeOrEqualDate } from '@intigral/utils/lib/helpers/datetime';

export const useAddonItem = (configuredAddonsList) => {
    // hooks
    const language = useLanguage();
    const { operator, subscriptionStatus = [], tierConfig } = user.getUserInfo() || {};
    const { data: configuration } = useConfiguration();
    const { data: subscriptionTiers } = useAllSubscriptionTier();
    const { data: bundleAndSaveRules } = useBundleAndSaveRules();
    const isGracePeriod = isUserInGracePeriod();
    const eligibleAddons = bundleAndSaveRules?.data?.eligibleAddons;
    const discountRules = bundleAndSaveRules?.data?.discountRules;
    // check current operator is eligible for pidk and choose
    const isPickAndChoose =
        configuration?.pickAndChoose?.enable && configuration?.pickAndChoose?.allowedOperators?.includes(operator);
    // get default service collection details
    const deaultServiceCollectionDetail = getDefaultServiceCollectionDetails(
        (subscriptionTiers?.planConfig as IPlanConfigV2)?.serviceCollectionDetails || []
    );

    // filter active subscribed addons
    let activeSubscribedAddons = [];
    if (isPickAndChoose) {
        activeSubscribedAddons = subscriptionStatus.filter((i) => i['statusName'] === 'ACTIVE');
    } else {
        activeSubscribedAddons = subscriptionStatus.filter(
            (i) =>
                i['statusName'] === 'ACTIVE' ||
                (i['statusName'] === 'CANCELLED' && isBeforeOrEqualDate(i?.nextRenewalDate))
        );
    }

    // current tier addons — may be missing while tiers load or for unknown tierGuid
    const currentTierAddons = subscriptionTiers?.tiers?.[tierConfig?.tierGuid]?.subscriptions ?? [];

    let subscribedAddonsGuids = [];
    if (isPickAndChoose) {
        const subscribedFreeAddons = getSubscrebedFreeAddons(activeSubscribedAddons, currentTierAddons);
        const subscribedPaidAddons = getSubscrebedPaidAddons(activeSubscribedAddons, currentTierAddons);
        subscribedAddonsGuids = [...subscribedFreeAddons, ...subscribedPaidAddons].map((i) => i.subscriptionGuid);
    } else {
        subscribedAddonsGuids = activeSubscribedAddons.map((i) => i.subscriptionGuid);
    }

    const carouselContent = [];
    // filter configuredAddonsList based on allowedOperators, valid images and subGuid match with current tier addons
    const filteredAddons = getFilteredAddons(configuredAddonsList ?? [], currentTierAddons, operator, language);

    filteredAddons.forEach((item) => {
        if (isStaticItem(item)) {
            if (item?.planName === PICK_AND_CHOOSE) {
                if (deaultServiceCollectionDetail !== null || deaultServiceCollectionDetail?.freeAddOnsCount > 0) {
                    const staticItem = transformStaticItem(item, language);
                    carouselContent.push(transformStaticCarouselItem(staticItem));
                }
            } else if (item?.planName === BUNDLE_AND_SAVE) {
                if (discountRules?.length > 0 ) {
                    const staticItem = transformStaticItem(item, language);
                    carouselContent.push(transformStaticCarouselItem(staticItem));
                }
            }
            return;
        }

        const backgroundImage = item.imageUrls?.find((img) => img.key === 'background');
        const titleImage = item.imageUrls?.find((img) => img.key === 'title');
        const logoUrl =
            language === 'ar'
                ? titleImage?.urlPNGAdaptive || titleImage?.urlAr || titleImage?.url || ''
                : titleImage?.urlPNGAdaptive || titleImage?.url || '';
        const poster =
            language === 'ar' ? backgroundImage?.urlAr || backgroundImage?.url || '' : backgroundImage?.url || '';
        if (poster.length <= 0) return;

        // check if addon is subscribed pick that else pick free
        const matches = currentTierAddons.filter((el) => el?.subscriptionGUID === item.subscriptionGuid);
        let addonDetails = null;
        // if isPickandChoose then check for free or paid availeable addons
        if (isPickAndChoose) {
            if (matches.length) {
                addonDetails =
                    matches.find((m) => m?.isSubscribed && !m?.serviceCollectionId) ||
                    matches.find((m) => m?.serviceCollectionId) ||
                    matches[0];
            } else {
                addonDetails = null;
            }
        } else {
            addonDetails = currentTierAddons.find((el) => el.subscriptionGUID === item.subscriptionGuid);
        }

        const amount = addonDetails?.amount ?? '';
        const currency = addonDetails?.currency ?? '';
        const serviceCollectionId = addonDetails?.serviceCollectionId ?? '';
        const isHardBundle =
            Object.prototype.hasOwnProperty.call(addonDetails ?? {}, 'isUnsubscribeAllowed') &&
            addonDetails?.isUnsubscribeAllowed === false;
        // const checkSubscribed = item.subscriptionGuid
        //     ? subscribedAddonsGuids?.includes(item.subscriptionGuid) || isHardBundle
        //     : false;
        let checkSubscribed;
        if (item?.subscriptionGuid?.startsWith('NETFLIX')) {
            checkSubscribed = subscribedAddonsGuids?.some((guid) => guid.startsWith('NETFLIX'));
        } else {
            checkSubscribed = subscribedAddonsGuids?.includes(item.subscriptionGuid) || isHardBundle;
        }

        // first check if pick and choose is disabled then don't show on us else check for serviceCollectionId or hardbundle
        const showOnUs = !isPickAndChoose ? false : serviceCollectionId || isHardBundle ? true : false;
        const isBundleAndSave = eligibleAddons?.some((rule) => rule?.addonGuid === item?.subscriptionGuid);

        const addonSubscription = item?.subscriptionGuid?.startsWith('NETFLIX')
            ? subscriptionStatus?.find((sub) => sub.subscriptionGuid?.startsWith('NETFLIX'))
            : subscriptionStatus?.find((sub) => sub.subscriptionGuid === item.subscriptionGuid);
        const isSuspendedAddon = addonSubscription?.statusName?.toUpperCase() === ADDON_STATUS.STATUS_SUSPENDED;
        const isGracePeriodAddon = isGracePeriod && checkSubscribed;
        const showPaymentFailed = isGracePeriodAddon || isSuspendedAddon;

        carouselContent.push({
            mainImage: poster,
            logo: logoUrl,
            status: showPaymentFailed ? __('payment_failed_title') : checkSubscribed ? __('subscribed') : '',
            ...(checkSubscribed &&
                !showPaymentFailed && {
                    statusColor: 'green',
                    statusIconName: 'check-green',
                    statusIconColor: 'green',
                }),
            ...(showPaymentFailed && {
                statusColor: 'red',
                statusIconName: 'exclamation-mark',
                statusIconColor: 'red',
            }),
            ...(item.subscriptionGuid &&
                !activeSubscribedAddons.includes(item.subscriptionGuid) && {
                    cost: !checkSubscribed && !isSuspendedAddon ? `${amount}` : '',
                    currency: showPaymentFailed ? '' : currency,
                }),
            isStatic: false,
            bannerType: item.bannerType,
            subscriptionGuid: item.subscriptionGuid,
            url: item?.url,
            serviceCollectionId,
            isSubscribed: checkSubscribed,
            isGracePeriodAddon,
            isSuspendedAddon,
            showOnUs,
            isBundleAndSave,
            isPickAndChoose,
            subscriptionTitle: addonDetails?.subscriptionTitle,
        });
    });

    const sortedCarouselContent = carouselContent.sort((a, b) => {
        if (a.isSubscribed && !b.isSubscribed) return 1;
        if (!a.isSubscribed && b.isSubscribed) return -1;
        return 0;
    });

    return {
        carouselContent: sortedCarouselContent,
        configuration,
        language,
        operator,
    };
};
